import { Button, Card, CardContent } from "@repo/ui";
import { Link } from "react-router-dom";

import { Container } from "@/components/container";

export function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[hsl(var(--hero))]">
        <Container className="pt-16 pb-12 md:pt-24 md:pb-20 grid gap-10 md:grid-cols-2 items-center">
          <div className="space-y-6">
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Bangkok SMB IT Consulting — Modernize, Secure, and Scale
            </h1>
            <p className="text-muted-foreground text-lg">
              Pro Systems helps small and medium businesses in Bangkok modernize infrastructure,
              accelerate cloud adoption, and improve reliability with pragmatic, cost-effective
              solutions.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="tel:0804685545"
                className="inline-flex items-center rounded-md bg-accent px-5 py-3 text-sm font-medium text-accent-foreground hover:opacity-90"
              >
                Call us
              </a>
              <Link to="/contact">
                <Button variant="outline">Contact us</Button>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-6 pt-4">
              <div>
                <div className="text-2xl font-semibold">150+</div>
                <div className="text-sm text-muted-foreground">SMB projects delivered</div>
              </div>
              <div>
                <div className="text-2xl font-semibold">99.9%</div>
                <div className="text-sm text-muted-foreground">Avg. uptime achieved</div>
              </div>
              <div>
                <div className="text-2xl font-semibold">24/7</div>
                <div className="text-sm text-muted-foreground">Support available</div>
              </div>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.pexels.com/photos/2296421/pexels-photo-2296421.jpeg"
              alt="Bangkok skyline with modern transport"
              className="rounded-lg border object-cover w-full h-[280px] md:h-[420px]"
            />
          </div>
        </Container>
      </section>

      {/* Services overview */}
      <section>
        <Container className="py-12 md:py-16">
          <div className="flex items-end justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-semibold">What we do</h2>
            <Link to="/services" className="text-sm text-accent hover:underline">
              All services
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {[
              {
                title: "IT Strategy & Roadmapping",
                desc: "Align technology to business goals with pragmatic, budget-aware plans.",
                to: "/services/it-strategy-roadmapping",
              },
              {
                title: "Cloud Migration",
                desc: "Plan and execute smooth migrations to AWS, Azure, or GCP without downtime.",
                to: "/services/cloud-migration",
              },
              {
                title: "Infrastructure & Networking",
                desc: "Secure, performant networks and on-prem/cloud infrastructure.",
                to: "/services/infrastructure-networking",
              },
              {
                title: "DevOps & Automation",
                desc: "CI/CD, IaC, and observability to boost reliability and speed.",
                to: "/services/devops-automation",
              },
            ].map((s) => (
              <Link to={s.to} key={s.to}>
                <Card>
                  <CardContent className="p-6">
                    <div className="font-medium mb-1">{s.title}</div>
                    <p className="text-sm text-muted-foreground">{s.desc}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* 3-step process */}
      <section className="bg-secondary/50">
        <Container className="py-12 md:py-16">
          <h2 className="text-2xl md:text-3xl font-semibold mb-8">How we work</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                n: "1",
                title: "Assess",
                desc: "Quick discovery of goals, risks, and current state.",
              },
              { n: "2", title: "Plan", desc: "Roadmap and budgets with clear milestones." },
              {
                n: "3",
                title: "Deliver",
                desc: "Implement, document, and support for long-term success.",
              },
            ].map((p) => (
              <Card key={p.n}>
                <CardContent className="p-6">
                  <div className="text-accent font-semibold">Step {p.n}</div>
                  <div className="mt-1 font-medium">{p.title}</div>
                  <p className="text-sm text-muted-foreground mt-2">{p.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </Container>
      </section>

      {/* Testimonial */}
      <section>
        <Container className="py-12 md:py-16">
          <Card>
            <CardContent className="p-6 md:p-8">
              <blockquote className="text-lg md:text-xl leading-relaxed">
                “Pro Systems migrated our workloads to the cloud with zero downtime and improved our
                performance by 40%. Their team is responsive and professional.”
              </blockquote>
              <div className="mt-4 text-sm text-muted-foreground">
                — Managing Director, Bangkok Retail SMB
              </div>
            </CardContent>
          </Card>
        </Container>
      </section>
    </div>
  );
}
